/**
 * Audit API functions
 */
import { apiClient } from "./client";

export interface AuditEventDto {
    id: string;
    eventType: string;
    actorUserId?: string;
    actorUserName?: string;
    targetUserId?: string;
    targetUserName?: string;
    teamId?: string;
    teamName?: string;
    metadata?: Record<string, unknown>;
    createdAt: string;
    createdBy?: string;
}

export interface AuditEventQueryParams {
    eventType?: string;
    actorUserId?: string;
    targetUserId?: string;
    teamId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
}

export interface AuditEventListResponse {
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
    items: AuditEventDto[];
}

export interface AuditStatsResponse {
    startDate?: string;
    endDate?: string;
    totalEvents: number;
    eventsByType: Record<string, number>;
    eventsByActor: Record<string, number>;
    eventsByTeam: Record<string, number>;
}

/**
 * Query audit events with filtering, sorting, and pagination
 */
export const fetchAuditEvents = async (
    params?: AuditEventQueryParams
): Promise<AuditEventListResponse> => {
    const response = await apiClient.get<AuditEventListResponse>("/audit", {
        params,
    });
    return response.data;
};

/**
 * Get a single audit event by ID
 */
export const fetchAuditEventById = async (
    id: string
): Promise<AuditEventDto> => {
    const response = await apiClient.get<AuditEventDto>(`/audit/${id}`);
    return response.data;
};

/**
 * Export audit events to CSV
 */
export const exportAuditEvents = async (
    params?: AuditEventQueryParams
): Promise<Blob> => {
    const response = await apiClient.get("/audit/export", {
        params,
        responseType: "blob",
    });
    return response.data;
};

/**
 * Get aggregated statistics for audit events
 */
export const fetchAuditStats = async (
    startDate?: string,
    endDate?: string
): Promise<AuditStatsResponse> => {
    const response = await apiClient.get<AuditStatsResponse>("/audit/stats", {
        params: { startDate, endDate },
    });
    return response.data;
};